'use client';

import { Box, Flex, IconButton, Input, Typography } from '@coolblue-development/freddy';
import { Magnifier } from '@coolblue-development/icons';
import { useTranslation } from '@i18n/useTranslation';
import { useDeferredValue, useMemo, useState } from 'react';

import { Facet as FacetType } from '../../types';
import Facet from './Facet';
import { isValidFilterType } from './helpers/isValidFilterType';
import SelectedFilters from './SelectedFilters';

function mapFacetToFilterGroup(facet: FacetType) {
  if (isValidFilterType(facet.filterType)) {
    return <Facet facet={facet} key={facet.facetName} />;
  }

  return undefined;
}

const filterFacetsBySearchTerm = (facets: FacetType[], searchTerm: string) => {
  if (!searchTerm.trim()) {
    return facets;
  }

  const searchString = searchTerm.toLowerCase();

  return facets.filter(facet => {
    if (facet.displayName?.toLowerCase().includes(searchString)) {
      return true;
    }

    return (
      facet.values?.some(value => value.displayName.toLowerCase().includes(searchString)) || false
    );
  });
};

export default function Filters({
  facets,
  allowFacetSearch = false
}: {
  facets: FacetType[];
  allowFacetSearch?: boolean;
}) {
  const { t } = useTranslation('webshop.filterPage.storeEmployeeFacetSearch');

  const [inputValue, setInputValue] = useState('');
  const deferredInputValue = useDeferredValue(inputValue);

  const facetsToDisplay = useMemo(() => {
    return deferredInputValue ? filterFacetsBySearchTerm(facets, deferredInputValue) : facets;
  }, [facets, deferredInputValue]);

  const mappedFacets = facetsToDisplay.map(mapFacetToFilterGroup);

  return (
    <>
      <SelectedFilters facets={facets} />

      <Flex direction='column' gap={600} mt={600}>
        {allowFacetSearch && (
          <Box>
            <Typography id='facet-search-title' mb={200} variant='h3'>
              {t('title')}
            </Typography>

            <Input
              aria-labelledby='facet-search-title'
              endAdornment={
                <IconButton aria-label={t('searchButtonLabel')} size='large'>
                  <Magnifier />
                </IconButton>
              }
              onChange={e => setInputValue(e.target.value)}
              placeholder={t('placeholder')}
              type='search'
              value={inputValue}
            />
          </Box>
        )}

        {mappedFacets}
        {allowFacetSearch && !mappedFacets.length && (
          <Typography variant='body' color='additional'>
            {t('noResults')}
          </Typography>
        )}
      </Flex>
    </>
  );
}
